import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  summarizeRecentEquipment,
  type RecentEquipment,
  type RecentEquipmentRow,
  type RemovedEquipment,
} from '../lib/recentEquipment';
import type { Equipment } from '../types/project';

const removedKey = (userId: string) => `trueload_quick_add_removed_${userId}`;

function readRemoved(userId: string): RemovedEquipment {
  try {
    const parsed = JSON.parse(localStorage.getItem(removedKey(userId)) ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * The signed-in user's recently entered proposed loads, for quick-add.
 *
 * Reads the user's own equipment rows across their projects (scoped through
 * the project's owner, so a firm member sees their own history, not a
 * colleague's), merged with the rows of the open project so something added a
 * moment ago is already re-addable. A failed read means no chips, never an
 * error in the contractor's face.
 *
 * Removed chips are remembered in this browser, per user.
 */
export function useRecentEquipment(
  userId: string | null | undefined,
  current: Equipment[],
  enabled: boolean
): { items: RecentEquipment[]; remove: (key: string) => void } {
  const [remote, setRemote] = useState<RecentEquipmentRow[]>([]);
  const [removed, setRemoved] = useState<RemovedEquipment>({});

  useEffect(() => {
    setRemoved(userId ? readRemoved(userId) : {});
  }, [userId]);

  useEffect(() => {
    if (!userId || !enabled) {
      setRemote([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('equipment')
          .select('equipment_name, equipment_type, source, amps, kw, voltage, created_at, projects!inner(user_id)')
          .eq('projects.user_id', userId)
          .eq('load_role', 'proposed')
          .order('created_at', { ascending: false })
          .limit(300);
        if (cancelled) return;
        if (error) {
          console.warn('[recentEquipment] read failed:', error.message);
          return;
        }
        setRemote((data ?? []) as unknown as RecentEquipmentRow[]);
      } catch (err) {
        if (!cancelled) console.warn('[recentEquipment] read threw:', err instanceof Error ? err.message : err);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, enabled]);

  const remove = useCallback((key: string) => {
    if (!userId) return;
    const next = { ...readRemoved(userId), [key]: new Date().toISOString() };
    try {
      localStorage.setItem(removedKey(userId), JSON.stringify(next));
    } catch {
      // Storage full or blocked: still hide it for this visit.
    }
    setRemoved(next);
  }, [userId]);

  const items = useMemo(() => {
    if (!enabled) return [];
    const local = current.filter(e => (e.load_role ?? 'proposed') === 'proposed');
    return summarizeRecentEquipment([...local, ...remote], 8, removed);
  }, [current, remote, removed, enabled]);

  return { items, remove };
}
